import axios from "axios";
import { getSession } from "./axios.config";

const axiosCall1 = async (method, url, data = "", headers = "") => {
  try {
    let options = {
      method,
      url,
    };
    if (headers) options.headers = headers;
    if (data) options.data = data;
    const response = await axios(options);
    return response?.data;
  } catch (err) {
    if (err && err.response && err.response.data) {
      const body = err.response.data;
      const thrown =
        typeof body === "object"
          ? { ...body, httpStatus: err.response.status }
          : body;
      throw thrown;
    } else
      return { code: 500, message: "Oops! Something went wrong.", data: null };
  }
};

export const checkHostByPartner = (username) => {
  return axiosCall1("GET", `/details/${username}`);
};

export const checkPartnerHealth = () => {
  return axiosCall1("GET", `/auth/vendor/health`);
};

export const userLogin = async (data) => {
  return axiosCall1("POST", "/auth/login", {
    ...data,
  });
};

// ---- reCAPTCHA v3 -----------------------------------------------------------
// v3 site key; the matching secret key lives on the backend only.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

let recaptchaScriptPromise = null;

// Injects the api.js script once; resolves false if it fails to load.
export const loadRecaptcha = () => {
  if (typeof window === "undefined" || !RECAPTCHA_SITE_KEY) {
    return Promise.resolve(false);
  }
  if (window.grecaptcha && window.grecaptcha.execute) {
    return Promise.resolve(true);
  }
  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        recaptchaScriptPromise = null;
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaScriptPromise;
};

// Returns a fresh single-use token (valid ~2 min) for the given action, or ""
// if reCAPTCHA is unavailable (no site key / blocked / failed).
export const getRecaptchaToken = async (action) => {
  const loaded = await loadRecaptcha();
  const grecaptcha = loaded && window.grecaptcha;
  if (!grecaptcha) return "";
  return new Promise((resolve) => {
    grecaptcha.ready(async () => {
      try {
        resolve(await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }));
      } catch (error) {
        console.log("reCAPTCHA execute failed", error);
        resolve("");
      }
    });
  });
};

// Merges a fresh reCAPTCHA token into a pre-auth request body. Throws when no
// token can be obtained (tokens are single-use, so every request gets its own).
const withRecaptcha = async (body) => {
  const recaptchaToken = await getRecaptchaToken("login");
  if (!recaptchaToken) {
    throw new Error(
      "Verification could not be completed. Please refresh the page and try again.",
    );
  }
  return { ...body, recaptchaToken };
};

// Same as withRecaptcha, but for GET requests that carry the token as a header.
const withRecaptchaHeader = async (headers = {}) => {
  const token = await getRecaptchaToken("login");
  if (!token) {
    throw new Error(
      "Verification could not be completed. Please refresh the page and try again.",
    );
  }
  return { ...headers, "x-recaptcha-token": token };
};

export const UserSignUp = async (data, code = "STOCKAL") => {
  return axiosCall1(
    "POST",
    "user/register",
    await withRecaptcha({
      ...data,
    }),
    {
      code: code,
    },
  );
};

export const UserPersonalDetails = async (data) => {
  return axiosCall1("PATCH", "user/personal-info", {
    ...data,
  });
};

export const UserAddress = async (data) => {
  return axiosCall1("POST", "/user/address", {
    ...data,
  });
};
export const userResetPassword = async (data) => {
  // return axiosCall1("POST", "/auth/reset-password", {
  //   ...data,
  // });
  const url = `${process.env.REACT_APP_BASE_API}/v1/auth/centralised-reset-password`;
  try {
    const response = await axios.post(url, data);
    return response;
  } catch (err) {
    if (err && err.response && err.response.data) throw err?.response?.data;
    else
      return { code: 500, message: "Oops! Something went wrong.", data: null };
  }
};
// 2FA calls are pre-auth, so they use the plain axios (full URL) instead of
// axiosCall1 (which goes through the token-refresh interceptor)
const twoFactorCall = async (path, body) => {
  const payload = await withRecaptcha(body);
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_API}/v1/auth/2fa/${path}`,
      payload,
    );
    return response.data;
  } catch (err) {
    throw err?.response?.data || err;
  }
};
export const sendTwoFactorOtp = ({ challengeId, channel }) =>
  twoFactorCall("send-otp", { challengeId, channel });
export const verifyTwoFactorOtp = ({ challengeId, channel, otp }) =>
  twoFactorCall("verify-otp", { challengeId, channel, otp });
export const changeTwoFactorPassword = ({ challengeId, newPassword }) =>
  twoFactorCall("change-password", { challengeId, newPassword });
export const userForgotPassword = async (data) => {
  // return axiosCall1("POST", "/auth/forgot-password", {
  // ...data,
  // });
  const url = `${process.env.REACT_APP_BASE_API}/v1/auth/centralised-forgot-password`;
  const payload = await withRecaptcha(data);
  try {
    const response = await axios.put(url, payload);
    return response;
  } catch (err) {
    if (err && err.response && err.response.data) throw err?.response?.data;
    else
      return { code: 500, message: "Oops! Something went wrong.", data: null };
  }
};
export const TransferFund = async (data) => {
  return axiosCall1("POST", "/transaction/initiate", {
    ...data,
  });
};

export const BeniCountryConfig = async ({ selectedMethod, formData }) => {
  const queryParams = new URLSearchParams();

  if (formData?.account_currency) {
    queryParams.append("currency", formData.account_currency);
  }

  if (formData?.country_code) {
    queryParams.append("country", formData.country_code);
  }

  if (selectedMethod) {
    queryParams.append("method", selectedMethod);
  }

  return axiosCall1(
    "GET",
    `metadata/bene-country-config?${queryParams.toString()}`,
  );
};

export const PostBeneficiary = async (data) => {
  return axiosCall1("POST", "/beneficiary", { ...data });
};

export const UserNameExist = async (userName, code = "STOCKAL") => {
  return axiosCall1(
    "GET",
    `/user/isUsernameAvailable?username=${userName}`,
    "",
    {
      code: code,
    },
  );
};
export const CountryList = async () => {
  return axiosCall1("GET", `/beneficiary/country`);
};
export const deleteBeneficiary = async (beneficiaryId) => {
  return axiosCall1(
    "DELETE",
    `/beneficiary/delete?beneficiaryId=${beneficiaryId}`,
  );
};

export const TransferApi = async ({
  buyAmount,
  sellAmount,
  toCurrency,
  fromCurrency,
  selectedMethodToTransfer,
  transactionType,
  country,
}) => {
  const queryParams = new URLSearchParams();
  if (buyAmount) queryParams.append("buyAmount", buyAmount);
  if (sellAmount) queryParams.append("sellAmount", sellAmount);
  if (toCurrency) queryParams.append("toCurrency", toCurrency);
  if (fromCurrency) queryParams.append("fromCurrency", fromCurrency);
  if (transactionType) queryParams.append("transactionType", transactionType);
  if (selectedMethodToTransfer)
    queryParams.append("method", selectedMethodToTransfer);
  queryParams.append("country", country ? country : "US");

  const endpoint = `/transaction/fee?${queryParams.toString()}`;
  // return {
  //   data: {
  //     totalFees: 200,
  //     rate: 1.2,
  //   },
  // };
  return axiosCall1("GET", endpoint);
};

export const PostWalletTransfer = async (data) => {
  return axiosCall1("POST", "/transaction/wallet/transfer", { ...data });
};

export const GetPinCodeDetails = async (data) => {
  return axiosCall1("GET", `/user/pincode?pincode=${data}`);
};

export const getDownloadTransaction = async (
  transactionNo,
  setSingleRowSuccess,
) => {
  try {
    const response = await axios({
      method: "get",
      url: "/transaction/receipt",
      params: { transactionNo },
      responseType: "blob",
    });

    const objectUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${transactionNo}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setSingleRowSuccess(false), 2000);
  } catch (err) {
    setSingleRowSuccess(false);
    console.error("Error processing the PDF:", err);
    throw err;
  }
};
export const getDownloadCsv = async ({
  toDate,
  fromDate,
  status,
  currency,
  transactionType,
  setIsSuccess,
}) => {
  try {
    const response = await axios({
      method: "get",
      url: "/transaction/download",
      params: {
        toDate: toDate,
        fromDate: fromDate,
        status: status,
        currency: currency,
        transactionType: transactionType,
      },
      responseType: "blob",
    });
    const objectUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `Transaction.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setIsSuccess(false), 2000);
  } catch (err) {
    setIsSuccess(false);
    console.error("Error processing the PDF:", err);
    throw err;
  }
};

export const getUser = async ({ queryKey }) => {
  // eslint-disable-next-line no-unused-vars
  const [_, params] = queryKey;
  const { userId } = params;
  return axiosCall1("GET", `user?userId=${userId}`);
};

export const globalAccount = async () => {
  return axiosCall1("GET", "/account/global");
};
export const WalletAccount = async () => {
  return axiosCall1("GET", "/account/wallet");
};

export const getCardDetails = async () => {
  return axiosCall1("GET", "/account/card");
};

export const requestCardStatusOTP = async () => {
  return axiosCall1("POST", "/account/card/send-otp");
};

export const changeCardStatus = async (status) => {
  return axiosCall1("POST", "/account/change-card-status", {
    status,
  });
};

export const getCardDetailsWithDOB = async (dob) => {
  return axiosCall1("POST", "/account/card-details", {
    dob: dob,
  });
};

export const TransactionDetails = async ({
  page,
  pageSize,
  toDate,
  fromDate,
  status,
  currency,
  transactionType,
  type,
}) => {
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", page);
  if (pageSize) queryParams.append("pageSize", pageSize);
  if (toDate) queryParams.append("toDate", toDate);
  if (fromDate) queryParams.append("fromDate", fromDate);
  if (status) queryParams.append("status", status);
  if (currency && currency !== "All") queryParams.append("currency", currency);
  if (transactionType && !type)
    queryParams.append("transactionType", transactionType);
  if (type) queryParams.append("type", "card");
  queryParams.append("accountType", "GLOBAL");

  const endpoint = `/transaction?${queryParams.toString()}`;

  return axiosCall1("GET", endpoint);
};

export const getBeneficiaryDetails = async ([
  _key,
  { status, beneficiaryId },
]) => {
  const params = new URLSearchParams({
    page: "1",
    pageSize: "500",
  });
  if (status) {
    params.append("status", status);
  }
  if (beneficiaryId) {
    params.append("beneficiaryId", beneficiaryId);
  }

  return axiosCall1("GET", `/beneficiary?${params.toString()}`);
};

export const Selfie = async () => {
  return axiosCall1("GET", "user/video/kyc");
};
export const SignUpOTP = async (formattedOTP, code = "STOCKAL") => {
  return axiosCall1("POST", "/user/account/otp/verify", await withRecaptcha(formattedOTP), {
    code: code,
  });
};

export const EmailIdentification = async (email, code = "STOCKAL") => {
  return axiosCall1(
    "GET",
    `/user/isExist?emailId=${encodeURIComponent(email)}`,
    "",
    await withRecaptchaHeader({ code }),
  );
};

export const AuthSubmit = async () => {
  return axiosCall1("PATCH", `user/submit`, {
    tnc: true,
  });
};

export const SendOtp = async (formData) => {
  return axiosCall1(
    "POST",
    `/user/otp/send`,

    formData,
  );
};
export const SendTransactionOtp = async (formData) => {
  return axiosCall1("POST", "/transaction/otp/send", formData);
};
export const SendForgotPasswordOtp = async (formData) => {
  return axiosCall1(
    "POST",
    "/user/forgot-password/otp/send",
    await withRecaptcha(formData),
  );
};
export const PassportUpload = async (side, formData) => {
  return axiosCall1(
    "POST",
    `user/upload-doc?fileName=Passport&docType=PASSPORT&side=${side}`,

    formData,

    {
      "Content-Type": "multipart/form-data",
    },
  );
};

// export const socketLogin = async (token) => {
//   const url = `${process.env.REACT_APP_SOCKET_ENDPOINT}/socketAuth`;
//   const body = {
//     authToken: token,
//   };
//   try {
//     let resp = await axios.post(url, body, {});
//     if (resp.status === 200) {
//       return resp.data.data;
//     }
//   } catch (err) {
//     throw err;
//   }
// };

export const getThemeContext = async (baseUrl) => {
  return axiosCall1(
    "GET",
    `/partner/theme-context?baseUrl=${encodeURIComponent(baseUrl)}`,
  );
};

// Global variable to store theme code (will be set by theme context)
let globalThemeCode = "STOCKAL";

// Function to set the global theme code (called by theme context)
export const setGlobalThemeCode = (code) => {
  globalThemeCode = code || "STOCKAL";
};

// Function to get the current theme code
export const getGlobalThemeCode = () => {
  return globalThemeCode;
};

// Wrapper functions that use the global theme code
export const UserSignUpWithTheme = async (data) => {
  return UserSignUp(data, getResolvedCode());
};

export const UserNameExistWithTheme = async (userName) => {
  return UserNameExist(userName, getResolvedCode());
};

export const SignUpOTPWithTheme = async (formattedOTP) => {
  return SignUpOTP(formattedOTP, getResolvedCode());
};

export const ValidatePasswordOtp = async (formattedOTP) => {
  return axiosCall1("POST", "/user/otp/verify", formattedOTP, {
    code: getResolvedCode(),
  });
};

export const EmailIdentificationWithTheme = async (email) => {
  return EmailIdentification(email, getResolvedCode());
};

export const uploadVideo = async (videoBlob, fileExtension = "webm") => {
  // Check if blob is valid
  if (!videoBlob || videoBlob.size === 0) {
    throw new Error("Video blob is empty or invalid");
  }

  const formData = new FormData();
  const fileName = `recording.${fileExtension}`;
  // Ensure blob has correct MIME type
  const blobWithType = new Blob([videoBlob], {
    type: `video/${fileExtension}`,
  });
  formData.append("file", blobWithType, fileName);

  try {
    const config = {
      timeout: 300000, // 5 minutes timeout
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        console.log("Upload Progress:", percentCompleted + "%");
      },
    };

    const response = await axios.post("user/video-upload", formData, config);
    return response?.data;
  } catch (err) {
    console.error("Upload error:", err);
    if (err && err.response && err.response.data) throw err?.response?.data;
    else if (err.code === "ECONNABORTED") {
      throw new Error(
        "Upload timed out. Please check your connection and try again.",
      );
    } else {
      throw new Error(err.message || "Oops! Something went wrong.");
    }
  }
};
export const faceDetection = async (key) => {
  return axiosCall1("POST", "user/face-detection", {
    key: key,
  });
};

export const getTransactionLimits = async () => {
  return axiosCall1("GET", "/account/transaction-control");
};

export const setTransactionLimits = async (controlLimits) => {
  return axiosCall1("POST", "/account/transaction-control", {
    controlLimits,
  });
};

export const logout = async () => {
  try {
    return await axiosCall1("POST", "/auth/logout", "", {
      Authorization: `Bearer ${getSession("accessToken")}`,
    });
  } catch (error) {
    console.log(error);
  }
};

// Get investing banner info for a user
export const getInvestingBannerInfo = async (custId) => {
  return axiosCall1("GET", `/user/acct-info?custId=${custId}`);
};

// Get fund/account summary for a user
export const getFundAccountSummary = async (custId) => {
  const url = `${process.env.REACT_APP_INVESTING_API}/v3/admin/account-summary?custId=${custId}`;
  try {
    const response = await axios.get(url);
    return response;
  } catch (err) {
    if (err && err.response && err.response.data) throw err?.response?.data;
    else
      return { code: 500, message: "Oops! Something went wrong.", data: null };
  }
};

// Generate SSO token for investing platform
export const generateInvestingSsoToken = async (isUnified = false) => {
  const token = getSession("accessToken");
  const data = {
    accessKey: token,
    fromAppName: "MCB",
    toAppName: isUnified ? "UNIFIED" : "INVESTING",
  };

  const url = `${process.env.REACT_APP_INVESTING_API}/v3/sso/generate-auth-code`;
  try {
    const response = await axios.post(url, data);
    return response;
  } catch (err) {
    if (err && err.response && err.response.data) throw err?.response?.data;
    else
      return { code: 500, message: "Oops! Something went wrong.", data: null };
  }
};

// verify SSO token for investing platform
export const getSsoToken = async (authCode) => {
  const url = `${process.env.REACT_APP_INVESTING_API}/v3/sso/token`;
  const data = { authCode: authCode };
  try {
    const response = await axios.post(url, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    if (err && err.response && err.response.data) throw err?.response?.data;
    else
      return { code: 500, message: "Oops! Something went wrong.", data: null };
  }
};

export const getUserDetails = async (userId) => {
  return axiosCall1("GET", `user?userId=${userId}`);
};

export const getPartnerWallet = async () => {
  return axiosCall1("GET", `account/partner-wallet`);
};

export const getBannerNotification = async () => {
  return axiosCall1("GET", `banner/notification/banner?app=MCA`);
};

export const updateBannerNotification = async (notificationId) => {
  return axiosCall1(
    "PATCH",
    `banner/notification/banner?notificationID=${notificationId}&app=MCA`,
  );
};

let globalRefCode = null;

export const setUrlRefCode = (refCode) => {
  globalRefCode = refCode;
};

export const getResolvedCode = () => {
  return globalRefCode || globalThemeCode || "STOCKAL";
};

export const isPartnerExist = async (refCode) => {
  return axiosCall1("GET", `/partner/exists?refCode=${refCode}`);
};


export const lienReportDownload = async (data) => {
  const url = `${process.env.REACT_APP_BASE_API}/v1/account/statement-letter?download=true`;
  try {
    const response = await axios.post(url, data, {
      responseType: "arraybuffer",
    });
       return response;
  } catch (error) {
    return error.response;
  }
};

export const balanceConfirmationDownload = async (userId, accountType) => {
  const url = `${process.env.REACT_APP_BASE_API}/v1/account/balance-confirmation?userId=${userId}&accountType=${accountType}`;

  try {
    const response = await axios.get(url, {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

export const accountStatementDownload = async (userId, accountType, fromDate, toDate, currency) => {
  const url = `${process.env.REACT_APP_BASE_API}/v1/account/account-statement?userId=${userId}&accountType=${accountType}&fromDate=${fromDate}&toDate=${toDate}&currency=${currency}`;

  try {
    const response = await axios.get(url, {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

export const stateList = async (countryCode) => {
  const url = `${process.env.REACT_APP_BASE_API}/v1/beneficiary/states?countryCode=${countryCode}`;

  try {
    const response = await axios.get(url);
    return response;
  } catch (error) {
    return error.response;
  }
};
